<template>
  <woot-modal class="role-form-modal" :show.sync="show" :on-close="onClose">
    <div
      class="h-auto overflow-auto flex flex-col"
      :class="isRTLView ? 'is-rtl' : 'is-ltr'"
    >
      <woot-modal-header-v2
        :header-title="headerTitle"
        :header-content="headerContent"
        :header-title-icon="headerTitleIcon"
        :custom-classes="'z-[60] border-b border-gray-100'"
        style="box-shadow: 0px 1px 4px #cacaca29"
      />

      <div class="!px-6">
        <form
          class="flex flex-col w-full items-start !py-4 !px-0 outside-scroll"
          :class="{ 'display-horizontal-overlay': displayHorizontalOverlay }"
          @scroll="onScroll"
        >
          <span v-show="activeTab === 'inboxes'" class="border-top-left" />
          <span v-show="activeTab === 'inboxes'" class="border-top-right" />
          <div class="w-full sticky">
            <label
              class="text-gray-800 leading-[18px] mb-1.5"
              :class="{ error: $v.roleName.$error }"
            >
              {{ $t('ROLES.TABLE.HEADER.NAME') }}
            </label>
            <input
              v-model.trim="roleName"
              type="text"
              maxlength="15"
              class="semantic-input"
              :placeholder="$t('ROLES.FORM.NAME.PLACEHOLDER')"
              :disabled="readOnlyName"
              @input="$v.roleName.$touch"
            />
            <div class="text-400 text-gray-600 text-sm mt-1.5">
              {{ $t('ROLES.FORM.NAME.DESC') }}
            </div>
          </div>

          <div class="tabs w-full sticky">
            <div
              class="tab"
              :class="{ active: activeTab === 'general' }"
              @click="activeTab = 'general'"
            >
              <span class="material-symbols-outlined">verified_user</span>
              <span>{{ $t('ROLES.TABS.GENERAL') }}</span>
            </div>
            <div
              class="tab"
              :class="{ active: activeTab === 'inboxes' }"
              @click="activeTab = 'inboxes'"
            >
              <span class="material-symbols-outlined">inbox</span>
              <span>{{ $t('ROLES.TABS.INBOX') }}</span>
            </div>
          </div>
          <div v-show="activeTab === 'inboxes'" class="sticky z-10 w-full">
            <div class="relative">
              <span class="border-top-left inside" />
              <span class="border-top-right inside" />
            </div>
          </div>

          <permission-by-group
            v-show="activeTab === 'general'"
            v-model="editablePermissions"
            :readonly="readOnly"
          />
          <inbox-permissions
            v-show="activeTab === 'inboxes'"
            v-model="editableInboxPermissions"
            :readonly="readOnly"
          />

          <div v-show="activeTab === 'inboxes'" class="sticky z-10 w-full">
            <div class="relative">
              <span class="border-bottom-left inside" />
              <span class="border-bottom-right inside" />
            </div>
          </div>
        </form>
      </div>

      <div
        class="flex flex-row justify-end gap-2 pt-4 pb-6 px-6 w-full z-[60] border-t border-gray-100"
        style="box-shadow: 0px -1px 4px #cacaca29"
      >
        <slot
          name="footer"
          :role-name="roleName"
          :permissions="editablePermissions"
          :inbox-permissions="editableInboxPermissions"
          :is-valid="!$v.roleName.$invalid"
        />
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import rtlMixin from 'shared/mixins/rtlMixin';
import PermissionByGroup from './PermissionByGroup.vue';
import InboxPermissions from './InboxPermissions.vue';

export default {
  components: { PermissionByGroup, InboxPermissions },
  mixins: [rtlMixin],
  props: {
    headerTitle: {
      type: String,
      required: true,
    },
    headerContent: {
      type: String,
      required: true,
    },
    headerTitleIcon: {
      type: String,
      default: 'add',
    },
    initialRoleName: {
      type: String,
      default: '',
    },
    initialPermissions: {
      type: Object,
      required: true,
    },
    initialInboxPermissions: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    readOnlyName: {
      type: Boolean,
      default: false,
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      roleName: this.initialRoleName,
      editablePermissions: this.initialPermissions,
      editableInboxPermissions: this.initialInboxPermissions,
      activeTab: 'general',
      show: true,
      scrollTop: 0,
    };
  },
  validations: {
    roleName: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(15),
    },
  },
  computed: {
    displayHorizontalOverlay() {
      return this.scrollTop > 200 && this.activeTab === 'inboxes';
    },
  },
  watch: {
    initialInboxPermissions(val) {
      this.editableInboxPermissions = val;
    },
  },
  methods: {
    onScroll(event) {
      this.scrollTop = event.target.scrollTop;
    },
  },
};
</script>

<style lang="scss" scoped>
.semantic-input {
  @apply rounded-[8px] border-[#D4D4D4] h-[44px] px-3 py-[10px] hover:border-gray-400 focus:border-gray-400 mb-0;
  @apply read-only:bg-[#F5F5F5] read-only:hover:border-[#D4D4D4] focus:border-[#D4D4D4];

  &::placeholder {
    @apply text-gray-500;
  }
}

::v-deep {
  &.role-form-modal {
    .modal-container {
      width: 1000px;
      border-radius: 12px;
    }
  }
}

.sticky {
  position: sticky;
  left: 0;
}

.tabs {
  display: flex;
  align-items: center;
  padding: 0;
  margin-top: 20px;
  margin-bottom: 16px;
}

.tab {
  @apply flex flex-row items-center px-6 py-5 cursor-pointer border-b border-gray-100 text-gray-700 gap-[10px];
  transition: all 0.3s ease;

  &:hover,
  &.active {
    @apply border-green-500 bg-[#F0FDF4];
  }

  &:hover {
    @apply border-green-500 bg-[#F0FDF4] text-[#22A06B];
  }

  &.active {
    @apply text-[#1F845A];
  }
}

.outside-scroll {
  @apply max-h-[calc(100vh-280px)] overflow-y-auto;

  @supports (-moz-appearance: none) {
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 transparent;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0px;
    left: 0px;
    width: 24px;
    height: 100%;
    background: white;
    z-index: 20;
  }

  .border-top-left {
    position: absolute;
    top: 109px;
    left: 24px;
    width: 18px;
    height: 18px;
    border-top-left-radius: 8px;
    background: #ff000000;
    z-index: 30;
    box-shadow: -10px -10px 0 2px #ffffff;
    display: none;
    &.inside {
      top: 0px;
      left: 0px;
      display: block;
    }
  }

  .border-top-right {
    position: absolute;
    top: 109px;
    right: 34px;
    width: 18px;
    height: 18px;
    border-top-right-radius: 8px;
    background: #ff000000;
    z-index: 30;
    box-shadow: 10px -10px 0 2px #ffffff;
    display: none;
    &.inside {
      top: 0px;
      right: 0px;
      display: block;
    }
  }

  .border-bottom-left {
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: 8px;
    height: 10px;
    border-bottom-left-radius: 8px;
    background: rgb(255 255 255);
    z-index: 30;
    box-shadow: -10px 10px 0 10px #ffffff;
    display: block;
    border-bottom: 1px solid #f3f4f6;
    border-left: 1px solid #f3f4f6;
  }

  .border-bottom-right {
    position: absolute;
    bottom: 0px;
    right: 1px;
    width: 10px;
    height: 10px;
    border-bottom-right-radius: 8px;
    background: rgb(255 255 255 / 0%);
    z-index: 30;
    box-shadow: 10px 10px 0 10px #ffffff;
    display: block;
    border-bottom: 1px solid #f3f4f6;
    border-right: 1px solid #f3f4f6;
  }

  &.display-horizontal-overlay {
    &::after {
      content: '';
      position: absolute;
      top: 93px;
      left: 0px;
      width: 100%;
      height: 16px;
      background: white;
      z-index: 20;
    }

    .border-top-left,
    .border-top-right {
      display: block;
    }
  }

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-[#c1c1c1] rounded-full;
    border: 3px solid transparent;
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
    border: 3px solid transparent;
    background-clip: padding-box;
  }
}

.is-rtl {
  .sticky {
    position: sticky;
    left: unset;
    right: 0px;
  }

  .outside-scroll {
    &::before {
      left: unset;
      right: 0px;
    }

    .border-top-left {
      left: 34px;
      &.inside {
        left: 0px;
      }
    }

    .border-top-right {
      right: 24px;
      &.inside {
        right: 0px;
      }
    }

    .border-bottom-left {
      background: transparent;
    }

    .border-bottom-right {
      bottom: 0px;
    }

    &.display-horizontal-overlay {
      &::after {
        left: unset;
        right: 0px;
      }
    }
  }
}
</style>
